import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { COLORS } from './styles';

/**
 * Defers mounting a dashboard panel until it scrolls into view.
 *
 * The dashboard mounted every panel at once, and each panel fetches on mount —
 * roughly a dozen API calls fired in parallel before anything was interactive,
 * for panels the user often never scrolls to. Because this gates *mounting*
 * rather than just fetching, a deferred panel costs nothing: no request, no
 * render, no Syncfusion child layout work.
 *
 * `rootMargin` starts the load before the panel is actually on screen, so in
 * practice it has usually finished by the time it is visible.
 *
 * Panels above the fold should pass `eager` — deferring those would add a frame
 * of placeholder for no benefit.
 */

const Placeholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: ${p => p.$minHeight}px;
  color: ${COLORS.muted};
  font-size: 11px;
  opacity: 0.5;
`;

const LazyPanel = ({ children, minHeight = 160, rootMargin = '300px', eager = false }) => {
    const ref = useRef(null);
    const [visible, setVisible] = useState(eager);

    useEffect(() => {
        if (visible) return undefined;

        // No IntersectionObserver (old browsers, jsdom in tests): render eagerly
        // rather than leaving a panel permanently blank.
        if (typeof IntersectionObserver === 'undefined') {
            setVisible(true);
            return undefined;
        }

        const node = ref.current;
        if (!node) return undefined;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some(e => e.isIntersecting)) {
                    setVisible(true);      // one-way: never unmount a loaded panel
                    observer.disconnect();
                }
            },
            { rootMargin }
        );

        observer.observe(node);
        return () => observer.disconnect();
    }, [visible, rootMargin]);

    return (
        <div ref={ref} style={{ width: '100%', height: '100%' }}>
            {visible ? children : <Placeholder $minHeight={minHeight}>Loading…</Placeholder>}
        </div>
    );
};

export default LazyPanel;
